import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import Logo from "../assets/logo.svg";
import DropDown from '../assets/drop-down.svg'
import { FileBadge, Menu } from "lucide-react";
import { useLazyLogoutAdminQuery } from "../services/auth/authApi";
import { clearAuthSession } from "../services/auth/authSession";
import { useAccess } from "../services/auth/AccessProvider";
import "@fontsource/inter/400.css";
import {
  ActivityLog,
  Bell,
  CourseManagement,
  Dashboard,
  DeleteHistroy,
  ManagePurchase,
  ManageReq,
  ManageUsers,
  Progress,
  Search,
} from "../components/AdminIcons";

const navigationItems = [
  { label: "Dashboard", href: "/admin/dashboard", icon: Dashboard, className: "w-6 h-6 sm:w-9 sm:h-9" },
  { label: "Manage User", href: "/admin/manage-user", icon: ManageUsers, className: "w-7 h-7 sm:w-10 sm:h-10" },
  {
    label: "Course Management",
    href: "/admin/course-management",
    icon: CourseManagement,
    className: "w-7 h-7 sm:w-10 sm:h-10",
  },
  {
    label: "Manage Requests",
    href: "/admin/requests",
    icon: ManageReq,
    className: "w-[30px] h-[30px] sm:w-[42px] sm:h-[42px]",
  },
  { label: "Manage Purchases", href: "/admin/purchases", icon: ManagePurchase, className: "w-[29px] h-[29px] sm:w-[41px] sm:h-[41px]" },
  { label: "Activity log", href: "/admin/activity-log", icon: ActivityLog, className: "w-[26px] h-[26px] sm:w-[38px] sm:h-[38px]" },
  {
    label: "Student Progress",
    href: "/admin/student-progress",
    icon: Progress,
    className: "w-6 h-6 sm:w-9 sm:h-9",
  },
  {
    label: "Deletion History",
    href: "/admin/deletion-history",
    icon: DeleteHistroy,
    className: "w-7 h-7 sm:w-10 sm:h-10",
  },
  {
    label: "Certificates",
    href: "/admin/certificates",
    icon: FileBadge,
    className: "w-6 h-6 sm:w-9 sm:h-9",
  },
];

function AdminLayout({ children }) {
  const { profile: adminProfile, canAccess } = useAccess();
  const firstName = adminProfile?.firstName || "";
  const lastName = adminProfile?.lastName || "";
  const roleName = adminProfile?.role || "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  const visibleNavigationItems = navigationItems.filter((item) =>
    canAccess(item.href),
  );
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`
    .toUpperCase()
    .trim() || "AD";

  const navigate = useNavigate();
  const [triggerLogout, { isFetching: isLoggingOut }] = useLazyLogoutAdminQuery();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const logoutMenuRef = useRef(null);

  const handleLogout = async () => {
    try {
      await triggerLogout().unwrap();
    } catch (error) {
    } finally {
      clearAuthSession();
      setIsLogoutOpen(false);
      navigate("/", { replace: true });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isLogoutOpen &&
        logoutMenuRef.current &&
        !logoutMenuRef.current.contains(event.target)
      ) {
        setIsLogoutOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLogoutOpen]);

  return (
    <div className="min-h-screen max-w-[1920px] flex">
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-60 sm:w-72 2xl:w-[346px] border border-[#F1EEEE] bg-[#F5F5F5] lg:shadow-[4px_4px_15.6px_0_#00000054] transition-transform duration-300 lg:translate-x-0 lg:sticky lg:top-0 lg:h-screen lg:inset-y-auto ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="flex  items-center gap-2.5 sm:gap-[14px] pl-4 sm:pl-6  2xl:pl-10 pt-3">
          <div className="w-9 sm:w-[50px] h-14 sm:h-[72px]">
            <img src={Logo} alt="logo" />
          </div>
          <div>
            <p className=" text-base sm:text-xl font-semibold leading-[23px] tracking-[6%] text-[#4A4A68]">
              STEAM Minds
            </p>
          </div>
        </div>

        <nav className="h-[calc(100vh-88px)] mt-2 overflow-y-auto">
          {visibleNavigationItems.map(({ label, href, icon: Icon, className }) => (
            <NavLink
              key={label}
              to={href}
              end={href === "/admin"}
              onClick={() => setIsSidebarOpen(false)}
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 sm:gap-4 2xl:gap-6 border border-[#F1EEEE] sm:text-lg pl-6 sm:pl-8 2xl:pl-[49px] py-3 2xl:py-[19px] transition-colors duration-200",
                  isActive
                    ? "bg-[#923D8C] text-white font-semibold"
                    : "hover:bg-[#faf9fd] hover:text-[#3f4365] text-[#4A4A68] font-medium",
                ].join(" ")
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={`${className} ${isActive ? "brightness-0 invert" : ""
                      }`}
                  />
                  <span className="tracking-[6%]">{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>

      {isSidebarOpen ? (
        <button
          type="button"
          aria-label="Close sidebar"
          className="fixed inset-0 z-30 bg-black/30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      ) : null}

      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 border-b border-[#938A9636] bg-white py-4 sm:pb-4 sm:pt-[34px] backdrop-blur-sm">
          <div className="flex  items-center gap-4 px-[22px] ">
            <button
              type="button"
              aria-label="Open sidebar"
              className="inline-flex h-11 w-11 items-center justify-center    text-[#596086] transition hover:bg-[#f7f5fc] lg:hidden"
              onClick={() => setIsSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </button>

            <div className="min-w-0">
              <h1 className="text-lg font-medium tracking-[6%] text-[#4A4A68]">
                Admin Dashboard
              </h1>
            </div>

            <div className="group hidden flex-1 justify-center px-3 xl:px-5 md:flex">
              <label className="flex w-full max-w-[468px] items-center justify-between rounded-full border border-[#938A96]  px-5 xl:px-10 py-3">
                <input
                  type="search"
                  placeholder="Search here"
                  className="w-full bg-transparent tracking-[6%] text-sm text-[#4f5274] outline-none placeholder:text-[#8C8CA1]"
                />
                <span className="group-focus-within:hidden">
                  <Search />
                </span>
              </label>
            </div>

            <div className="ml-auto sm:mr-4 xl:mr-8 flex items-center gap-3 sm:gap-5 xl:gap-[34px]">
              {/* <button
                type="button"
                aria-label="Notifications"
                className="relative flex h-9 sm:h-11 w-8 sm:w-10 items-center justify-center rounded-full text-[#596086] transition hover:bg-[#f7f5fc]"
              >
                <Bell />

                <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-[#EF4444] sm:hidden" />

                <span className="absolute right-1 top-1 hidden min-w-5 items-center justify-center rounded-full bg-[#EF4444] px-1 text-xs text-white sm:flex sm:h-5">
                  4
                </span>
              </button> */}

              <div ref={logoutMenuRef} className="relative">
                <button
                  type="button"
                  className="flex items-center gap-2 xl:gap-4 rounded-full text-left transition"
                  onClick={() => setIsLogoutOpen((open) => !open)}
                >
                  <div className="h-8 w-8 sm:h-10 sm:w-10 overflow-hidden rounded-full  bg-slate-400">
                    <div className="flex h-full w-full items-center justify-center text-xs font-semibold text-white">
                      {initials}
                    </div>
                  </div>
                  <div className="hidden text-left sm:block">
                    <p className="text-xs font-medium text-[#374151]">
                      {fullName}
                    </p>
                    {roleName ? (
                      <p className="text-[11px] font-medium capitalize text-[#6B7280]">
                        {roleName}
                      </p>
                    ) : null}
                  </div>
                  <img src={DropDown} alt="drop down icon" className="hidden sm:block" />
                </button>

                {isLogoutOpen ? (
                  <div className="absolute -right-4 xl:right-0 top-[54px] z-50 w-[150px] bg-[#F5F5F5] shadow-[0_8px_24px_rgba(0,0,0,0.18)]">
                    <button
                      type="button"
                      disabled={isLoggingOut}
                      className="block w-full px-4 py-[7px] text-left font-['Inter'] text-xs font-normal leading-4 text-[#D00000]"
                      onClick={handleLogout}
                    >
                      {isLoggingOut ? "Logging out..." : "Logout"}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </header>

        <main className="font-['Inter']  bg-[#D9D9D914]">{children}</main>
      </div>
    </div>
  );
}

export default AdminLayout;
